import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import humanizeDuration from 'humanize-duration';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import * as qs from 'qs';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import LinkRouter from '../../common/LinkRouter';
import LoadingIndicator from '../../common/LoadingIndicator';
import FaroParametersTable from '../../faro/parameters/FaroParametersTable';
import ExecutionActionStatusChip from './ExecutionActionStatusChip';
import ExecutionTestActionStateParser from './ExecutionTestActionStateParser';
import ExecutionTestStatusChip from './ExecutionTestStatusChip';
import TestLogsDialog from './TestLogsDialog';
import { ActionState } from './ExecutionConstants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    header: {
      marginBottom: 24,
      display: 'flex',
      alignItems: 'center'
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03),
      marginBottom: 24
    },
    alert: {
      margin: '16px 0'
    },
    headerText: {
      marginLeft: 16
    },
    durationTableCell: { 
      minWidth: 150 
    },
    stdOutLink: { 
      marginRight: 8 
    }
  })
);

const windsorApi = new WindsorApi();

function ExecutionTestActionDetailsPage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match, history, location } = props;
  const familyId = match.params.familyId;
  const executionId = match.params.executionId;
  const actionName = match.params.actionName;
  const { tab = 'results' } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const classes = useStyles({});

  const [execution, setExecution] = useState(null);
  const [errors, setErrors] = useState([]);
  const [parameters, setParameters] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [tests, setTests] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState(null);

  useEffect(() => {
    if (!execution) {
      windsorApi.getExecution(familyId, executionId).then(execution => {
        setExecution(execution);
        const actionFromWorkflowCache = execution.WorkflowCache.TestFunctions.find(item => item.ActionName === actionName);
        if (actionFromWorkflowCache && actionFromWorkflowCache.Parameters) {
          const paramKeys = Object.keys(actionFromWorkflowCache.Parameters);
          setParameters(paramKeys.length ? paramKeys.map(key => ({ name: key, value: actionFromWorkflowCache.Parameters[key] })) : null);
        }
      });
    }
  }, [execution, familyId, executionId, actionName]);

  useEffect(() => {
    if (execution) {
      setActionStatus(new ExecutionTestActionStateParser(execution).getActionState(actionName));
      const action = execution.TestSets.find(item => item.ActionName === actionName);
      if (action) {
        setTests(action.Tests.map(test => ({
          status: test.TestStatus,
          name: test.TestName,
          description: test.TestDescription,
          output: test.TestOutput,
          duration: test.TimeStamp ? (parseInt(test.TimeStamp) - parseInt(action.StartTime)) * 1000 : null,
          stdOutLogs: test.TestLogs.StdOut,
          stdErrLogs: test.TestLogs.StdErr,
          hasStdOut: test.TestLogs.StdOut.some(log => !!log),
          hasStdErr: test.TestLogs.StdErr.some(log => !!log)
        })));
        if (action.Errors) {
          setErrors(action.Errors);
        }
      }
    }
  }, [execution, actionName]);

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push({ search: `?tab=${newValue}` });
  };

  return execution && actionStatus ? (
    <div className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb">
        <LinkRouter color="textSecondary" to={`/families`}>
          Families
          </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
          {execution.FamilyCache.Name}
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows`}>
          Workflows
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}`}>
          {execution.WorkflowName}
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}?tab=executions`}>
          Executions
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/executions/${execution.GUID}`}>
          {execution.GUID}
        </LinkRouter>
        <Typography color="textPrimary">{actionName}</Typography>
      </Breadcrumbs>
      <Divider className={classes.divider} />
      <div className={classes.header}>
        <ExecutionActionStatusChip state={actionStatus} />
        <Typography variant="h6" className={classes.headerText}>
          Test details for action: {actionName}
        </Typography>
      </div>
      {errors && errors.length > 0 && <Alert severity="error" className={classes.alert}>
        {errors.map((error, index) => <Typography key={index}>{error}</Typography>)}
      </Alert>}
      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={onTabChange}
        aria-label="Action Details Tabs"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Test Results" value="results" />
        <Tab label="Parameters" value="parameters" />
      </Tabs>
      {tab === 'results' && <div>
        {tests && tests.length > 0 && <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Run Time</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Logs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map(row => (
                <TableRow key={row.name}>
                  <TableCell>
                    <ExecutionTestStatusChip status={row.status} />
                  </TableCell>
                  <TableCell>
                    {row.name}
                  </TableCell>
                  <TableCell className={classes.durationTableCell}>
                    {row.duration ? humanizeDuration(row.duration) : '--'}
                  </TableCell>
                  <TableCell>
                    {row.output}
                  </TableCell>
                  <TableCell>
                    {row.hasStdOut && <Link
                      className={classes.stdOutLink}
                      component="button"
                      variant="body1"
                      onClick={() => setSelectedLogs({ testName: row.name, logType: 'stdout', logs: row.stdOutLogs })}
                    >
                      stdout
                    </Link>}
                    {row.hasStdErr && <Link
                      component="button"
                      variant="body1"
                      onClick={() => setSelectedLogs({ testName: row.name, logType: 'stderr', logs: row.stdErrLogs })}
                    >
                      stderr
                    </Link>}
                    {!row.hasStdOut && !row.hasStdErr && <Typography color="textSecondary">None</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
        {tests && tests.length === 0 && actionStatus !== ActionState.InProgress && <Typography color="textSecondary">No tests were executed</Typography>}
        {(!tests || (tests.length === 0 && actionStatus === ActionState.InProgress)) && <Alert severity="info" className={classes.alert}>
          <Typography color="textSecondary">Test results will be available after the test has completed</Typography>
        </Alert>}
      </div>}
      {tab === 'parameters' && <div>
        {parameters && <FaroParametersTable hideKmsKeyArn items={parameters} />}
        {!parameters && <Typography color="textSecondary">This action has no parameters</Typography>}
      </div>}
      {selectedLogs && <TestLogsDialog
        testName={selectedLogs.testName}
        logType={selectedLogs.logType}
        logs={selectedLogs.logs}
        onClose={() => setSelectedLogs(null)}
      />}
    </div >
  ) : <LoadingIndicator loading={true} />;
};

export default withRouter(withSnackbar(ExecutionTestActionDetailsPage));
